<script lang="ts">
	import { page } from '$app/state';
	import Icon from '@iconify/svelte';
	import Modal from './Modal.svelte';

	let { open, onclose }: { open: boolean; onclose: () => void } = $props();

	type LegendEntry = { letter: string; frame: string; meaning: string };

	type HelpContent = {
		title: string;
		rules: string[];
		legend?: LegendEntry[];
	};

	function tile(f: string) {
		return {
			outer:     `bg-(${f}-outer)`,
			highlight: `bg-(${f}-highlight)`,
			shadow:    `bg-(${f}-shadow)`,
			fill:      `bg-(${f}-fill)`,
			text:      `text-(${f}-text)`
		};
	}

	const HELP_BY_PATH: Record<string, HelpContent> = {
		'/word-guess': {
			title: 'How to play Word Guess',
			rules: [
				'Guess the hidden 5-letter word in 6 tries.',
				'After each guess, tiles change color to show how close you were.',
				'A new word is available each day.'
			],
			legend: [
				{ letter: 'W', frame: '--correct-frame', meaning: 'Correct letter, correct spot' },
				{ letter: 'O', frame: '--present-frame', meaning: 'Letter is in the word, wrong spot' },
				{ letter: 'X', frame: '--absent-frame',  meaning: 'Letter is not in the word' }
			]
		},
		'/word-build': {
			title: 'How to play Word Build',
			rules: [
				'Build words using the 7 given letters.',
				'Every word must include the center letter and be at least 4 letters long.',
				'Letters can be used more than once.',
				'Score points for each valid word. Pangrams use all 7 letters!',
				'Reach the Great rank to finish the day. Find every word for Wordsmith!'
			],
			legend: [
				{ letter: 'A', frame: '--present-frame', meaning: 'Center letter. Must appear in every word' },
				{ letter: 'B', frame: '--guess-frame',  meaning: 'Outer letter. Use as many times as needed' }
			]
		},
		'/word-group': {
			title: 'How to play Word Group',
			rules: [
				'Sort 16 words into 4 groups of 4 related words.',
				'Select 4 words you think belong together, then submit.',
				'Tile color shows group difficulty once solved.',
				'You have 4 mistakes before the game ends.',
				'Hold down a tile to see its definition.'
			],
			legend: [
				{ letter: '1', frame: '--present-frame', meaning: 'Easiest group' },
				{ letter: '2', frame: '--correct-frame', meaning: 'Medium difficulty' },
				{ letter: '3', frame: '--down-frame',    meaning: 'Hard group' },
				{ letter: '4', frame: '--absent-frame',  meaning: 'Hardest group' }
			]
		},
		'/word-cross': {
			title: 'How to play Word Cross S',
			rules: [
				'Fill in the crossword grid using the across and down clues.',
				'Click a clue or cell to select it, then type your answer.',
				'Tab or arrow keys to move between cells.',
				'Use Check to reveal errors, or Reveal to show all answers.'
			],
			legend: [
				{ letter: 'A', frame: '--guess-frame',  meaning: 'Empty cell' },
				{ letter: 'B', frame: '--present-frame', meaning: 'Active across clue' },
				{ letter: 'C', frame: '--down-frame',    meaning: 'Active down clue' },
				{ letter: '!', frame: '--absent-frame',  meaning: 'Incorrect letter' }
			]
		},
		'/word-cross-m': {
			title: 'How to play Word Cross M',
			rules: [
				'A bigger crossword built from four interlocking mini grids.',
				'Click a clue or cell to select it, then type your answer.',
				'Tab or arrow keys to move between cells.',
				'Use Check to reveal errors, or Reveal to show all answers.'
			],
			legend: [
				{ letter: 'A', frame: '--guess-frame',  meaning: 'Empty cell' },
				{ letter: 'B', frame: '--present-frame', meaning: 'Active across clue' },
				{ letter: 'C', frame: '--down-frame',    meaning: 'Active down clue' },
				{ letter: '!', frame: '--absent-frame',  meaning: 'Incorrect letter' }
			]
		},
		'/word-crypto': {
			title: 'How to play Word Crypto',
			rules: [
				'Each letter in the phrase has been swapped for a number.',
				'Tap a numbered tile, then type a letter to assign it across every matching tile.',
				'Same number = same letter. Backspace clears the active tile.',
				'You have 4 mistakes before the game ends. Use hints to reveal a tricky letter.'
			],
			legend: [
				{ letter: '',  frame: '--guess-frame',   meaning: 'Unassigned tile' },
				{ letter: 'A', frame: '--present-frame', meaning: 'Selected tile' },
				{ letter: 'A', frame: '--down-frame',    meaning: 'Other tiles with the same number' },
				{ letter: 'A', frame: '--correct-frame', meaning: 'Filled in with a guess' }
			]
		},
		'/word-speed': {
			title: 'How to play Word Speed',
			rules: [
				'Type the daily passage one word at a time before the clock runs out.',
				'Words advance automatically the moment what you type matches, no Enter or Space.',
				'Score points for every word; longer words are worth more.',
				'One timed run per day. Your score, words, WPM and accuracy are recorded.'
			],
			legend: [
				{ letter: 'A', frame: '--guess-frame',   meaning: 'Upcoming letter' },
				{ letter: 'A', frame: '--down-frame',    meaning: 'Current word' },
				{ letter: 'A', frame: '--present-frame', meaning: 'Typed correctly' },
				{ letter: 'A', frame: '--absent-frame',  meaning: 'Wrong letter' }
			]
		},
		'/num-fill': {
			title: 'How to play Num Fill',
			rules: [
				'Fill each 3×3 set with the numbers 1–9.',
				'Tap a cell in any set, then select a number.',
				'Numbers in sets, rows or columns cannot repeat.',
				'You have 5 mistakes before the game ends.',
				'Turn on Notes to pencil in several options for a cell, or use one of your 3 hints for the next logical cell.'
			],
			legend: [
				{ letter: '5', frame: '--guess-frame',   meaning: 'A given clue' },
				{ letter: '5', frame: '--present-frame', meaning: 'Selected cell' },
				{ letter: '5', frame: '--absent-frame',  meaning: 'Breaks a row, column or set' }
			]
		},
		'/word-find': {
			title: 'How to play Word Find',
			rules: [
				'Trace connected letters to find themed words.',
				'Every letter on the board belongs to exactly one word.',
				'The Anchor is a special word that spans the full board.',
				'Tap 3 incorrect words to unlock a hint.'
			],
			legend: [
				{ letter: 'A', frame: '--guess-frame',  meaning: 'Untraced letter' },
				{ letter: 'B', frame: '--correct-frame', meaning: 'Found theme word' },
				{ letter: 'A', frame: '--down-frame',    meaning: 'Anchor. Spans the full board' }
			]
		},
		'/word-tower': {
			title: 'How to play Word Tower',
			rules: [
				'Drag or tap connected tiles (diagonals count) to spell words of 3+ letters.',
				'Each valid word clears its tiles; everything above drops down.',
				'Words of 5+ letters also clear their entire row.',
				'A new row rises from the bottom on a timer. Don’t let the tower reach the top!',
				'One run per day. Your final score is recorded.'
			],
			legend: [
				{ letter: 'A', frame: '--guess-frame',   meaning: 'Tower tile' },
				{ letter: 'A', frame: '--present-frame', meaning: 'Selected tile' }
			]
		}
	};

	const DEFAULT_HELP: HelpContent = {
		title: 'Bubble Games',
		rules: ['Five daily word puzzles, with a new set every day.', 'Track your streaks and history from the Stats and Archive pages.']
	};

	let content = $derived(HELP_BY_PATH[page.url.pathname] ?? DEFAULT_HELP);

	const FILL_DURATION_MS = 7000;
	let fillPct = $state(0);
	let ready = $state(false);
	let secondsLeft = $state(FILL_DURATION_MS / 1000);

	$effect(() => {
		if (open) {
			fillPct = 0;
			ready = false;
			secondsLeft = FILL_DURATION_MS / 1000;
			const raf = requestAnimationFrame(() => { fillPct = 100; });
			const interval = setInterval(() => {
				secondsLeft = Math.max(0, secondsLeft - 1);
			}, 1000);
			const t = setTimeout(() => { ready = true; }, FILL_DURATION_MS);
			return () => {
				cancelAnimationFrame(raf);
				clearInterval(interval);
				clearTimeout(t);
			};
		}
	});

	function tryClose() {
		if (ready) onclose();
	}
</script>

<Modal title={content.title} {open} onclose={tryClose} dismissable={ready} bareFooter>
	<div class="flex flex-col gap-4">
		<ul class="flex flex-col gap-2">
			{#each content.rules as rule}
				<li class="flex gap-2 text-sm">
					<span class="select-none opacity-60" aria-hidden="true">•</span>
					<span>{rule}</span>
				</li>
			{/each}
		</ul>

		{#if page.url.pathname === '/word-group'}
			<div class="flex flex-col gap-px">
				<div class="h-px bg-(--guess-frame-outer)/70"></div>
				<div class="h-px bg-(--guess-frame-highlight)/25"></div>
			</div>
			<div class="flex items-center gap-2">
				<div class="relative h-10 shrink-0">
					<div class="pixel-frame relative flex h-full items-center px-3">
						<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
						<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
						<div class="pixel-frame absolute inset-1 bg-(--bottom-nav-frame-shadow)"></div>
						<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-fill)"></div>
						<span class="relative text-xs font-bold uppercase text-(--bottom-nav-frame-text)">APPLE</span>
					</div>
					<Icon icon="mdi:gesture-tap-hold" class="absolute -right-2 -bottom-2 size-5 text-(--correct-frame-fill)" />
				</div>
				<Icon icon="mdi:arrow-right-thin" class="size-5 shrink-0 opacity-60" />
				<div class="pixel-frame relative flex-1 overflow-hidden">
					<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
					<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
					<div class="pixel-frame absolute inset-1 bg-(--bottom-nav-frame-shadow)"></div>
					<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-fill)"></div>
					<p class="relative px-3 py-2 text-xs italic text-(--bottom-nav-frame-text)">
						“A round fruit with red or green skin and white flesh.”
					</p>
				</div>
			</div>
		{/if}

		{#if content.legend}
			<div class="flex flex-col gap-px">
				<div class="h-px bg-(--guess-frame-outer)/70"></div>
				<div class="h-px bg-(--guess-frame-highlight)/25"></div>
			</div>
			<div class="flex flex-col gap-2">
				{#each content.legend as entry}
					{@const t = tile(entry.frame)}
					<div class="flex items-center gap-3 text-sm">
						<div class="relative size-9 shrink-0">
							<div class="pixel-frame absolute inset-0 {t.outer}"></div>
							<div class="pixel-frame absolute inset-0.5 {t.highlight}"></div>
							<div class="pixel-frame absolute inset-1.5 {t.shadow}"></div>
							<div class="pixel-frame absolute inset-2 {t.fill}"></div>
							<div class="pixel-frame absolute inset-2.5 {t.shadow}"></div>
							<div class="pixel-frame absolute inset-2 flex items-center justify-center text-sm font-bold uppercase {t.text}">{entry.letter}</div>
						</div>
						<span>{entry.meaning}</span>
					</div>
				{/each}
			</div>
		{/if}
	</div>

	{#snippet footer()}
		<button type="button" disabled={!ready} onclick={tryClose} class="welcome-create-bar pixel-frame relative block w-full overflow-hidden transition-transform {ready ? 'cursor-pointer active:translate-y-px' : 'cursor-default'}">
			<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
			<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
			<div class="pixel-frame absolute inset-1 bg-(--bottom-nav-frame-shadow)"></div>
			<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-fill)"></div>
			<div class="absolute inset-1.5 overflow-hidden">
				<div class="account-fill pixel-frame-flat-left absolute inset-y-0 left-0 overflow-hidden" style:width="{fillPct}%">
					{#if ready}<div class="glint"></div>{/if}
				</div>
			</div>
			<div class="relative flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold uppercase {ready ? 'text-(--correct-frame-text)' : 'text-(--bottom-nav-frame-text)'}">
				<Icon icon="mdi:check" class="size-4" />
				Got it{!ready ? ` (${secondsLeft})` : ''}
			</div>
		</button>
	{/snippet}
</Modal>

<style>
	.account-fill {
		background: linear-gradient(
			to bottom,
			var(--correct-frame-highlight) 0px,
			var(--correct-frame-highlight) 2px,
			var(--correct-frame-fill) 2px,
			var(--correct-frame-fill) 100%
		);
		transition: width 7000ms linear;
	}
	.glint {
		position: absolute;
		inset-block: 0;
		width: 35%;
		background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.5) 50%, transparent 100%);
		animation: glint-sweep 2.5s ease-in-out infinite;
	}
	@keyframes glint-sweep {
		0%   { left: -35%; opacity: 0; }
		15%  { opacity: 1; }
		85%  { opacity: 1; }
		100% { left: 120%; opacity: 0; }
	}
</style>
